class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(
        this.dataset.index,
        0,
        undefined,
        undefined,
        this.dataset.cartItemKey,
        this.dataset.cityCustomPolarisedAddonKey
      );
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId,
      event.target.dataset.cartItemKey,
      event.target.dataset.cityCustomPolarisedAddonKey
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId, itemKey, customPolarisedAddonKey) {
    this.enableLoading(line);

    const hasCustomPolarisedPair = itemKey && customPolarisedAddonKey;
    const requestedQuantity = Number(quantity);
    const bodyPayload = {
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };
    let cartEndpoint = routes.cart_change_url;

    if (hasCustomPolarisedPair) {
      bodyPayload.updates = {
        [itemKey]: Number(quantity),
        [customPolarisedAddonKey]: Number(quantity),
      };
      cartEndpoint = routes.cart_update_url;
    } else {
      if (itemKey) bodyPayload.id = itemKey;
      else bodyPayload.line = line;
      bodyPayload.quantity = quantity;
    }

    const body = JSON.stringify(bodyPayload);

    fetch(`${cartEndpoint}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        let parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const sectionsToRender = this.getSectionsToRender();
        const syncTransitProtection =
          window.CityCartEdit && typeof window.CityCartEdit.syncTransitProtectionBeforeCartRender === 'function'
            ? window.CityCartEdit.syncTransitProtectionBeforeCartRender(
                parsedState,
                sectionsToRender.map((section) => section.section),
                window.location.pathname
              )
            : Promise.resolve(parsedState);

        return syncTransitProtection.then((syncedState) => {
          if (syncedState && syncedState.sections) parsedState = syncedState;

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        sectionsToRender.forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });

        if (window.CityCartEdit && typeof window.CityCartEdit.initEnhancements === 'function') {
          window.CityCartEdit.initEnhancements(parsedState);
        }

        const updatedItem = itemKey && Array.isArray(parsedState.items)
          ? parsedState.items.find((item) => item.key === itemKey)
          : null;
        const fallbackUpdatedItem = Array.isArray(parsedState.items)
          ? parsedState.items.find((item) => Number(item.variant_id || 0) === Number(variantId || 0)) || parsedState.items[line - 1]
          : null;
        const updatedValue = (updatedItem || fallbackUpdatedItem) ? (updatedItem || fallbackUpdatedItem).quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== requestedQuantity) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        this.updateLiveRegions(line, window.cartStrings.error);
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      this.querySelector(`#Line-item-error-${line}`) || this.querySelector(`#CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}

if (!window.__cityRxCartGroupActions) {
  window.__cityRxCartGroupActions = true;
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-city-rx-remove-keys]');
    if (!button || button.getAttribute('aria-busy') === 'true') return;
    const keys = String(button.dataset.cityRxRemoveKeys || '').split('|').filter(Boolean);
    if (!keys.length) return;
    event.preventDefault();
    event.stopPropagation();
    const group = button.closest('[data-city-rx-group-row]');
    const error = group && group.querySelector('[data-city-rx-group-error]');
    const updates = {};
    let originalItems = [];
    keys.forEach((key) => { updates[key] = 0; });
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    const cartJsonUrl = `${window.Shopify?.routes?.root || '/'}cart.js`;
    const cartAddUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;
    const restoreOriginalGroup = () => {
      if (!originalItems.length) return Promise.resolve();
      return fetch(cartJsonUrl, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cartState) => {
          const currentKeys = new Set((cartState.items || []).map((item) => item.key));
          const missingItems = originalItems.filter((item) => !currentKeys.has(item.key));
          if (!missingItems.length) return null;
          return fetch(cartAddUrl, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: missingItems.map((item) => ({
                id: item.variant_id,
                quantity: item.quantity,
                properties: item.properties,
              })),
            }),
          }).then((response) => response.json());
        });
    };

    fetch(cartJsonUrl, { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cartState) => {
        originalItems = (cartState.items || []).filter((item) => keys.includes(item.key));
        if (originalItems.length !== keys.length) throw new Error('Prescription configuration could not be verified');
        return fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            updates,
            sections: 'cart-drawer,cart-icon-bubble,main-cart-items,main-cart-footer',
            sections_url: window.location.pathname,
          }),
        });
      })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'Unable to remove configuration');
        const remainingKeys = new Set((response.items || []).map((item) => item.key));
        if (keys.some((key) => remainingKeys.has(key))) throw new Error('Prescription configuration was not fully removed');
        if (Array.isArray(window.dataLayer)) {
          window.dataLayer.push({
            event: 'rx_cart_group_removed',
            configuration_id: String(button.dataset.cityRxConfigId || ''),
          });
        }
        if (document.getElementById('main-cart-items')) {
          window.location.reload();
          return;
        }
        if (window.CityCartEdit && typeof window.CityCartEdit.refreshCartDrawer === 'function') {
          return window.CityCartEdit.refreshCartDrawer(true, 0);
        }
        window.location.href = routes.cart_url;
      })
      .catch((requestError) => restoreOriginalGroup().catch(() => null).then(() => {
        console.warn('Prescription-sunglasses group removal failed.', requestError);
        button.removeAttribute('aria-busy');
        button.disabled = false;
        if (error) {
          error.textContent = button.dataset.cityRxRemoveError || 'We couldn’t remove all the items for these glasses. Please try again.';
          error.hidden = false;
          error.focus();
        }
      }));
  }, true);
}
